import React, { useState, useEffect, useRef, useMemo } from "react";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../applicationUi/components/Table";
import { logInfo } from "../../utils/logger";
import { fetchInvoiceById, fetchInvoicesByClientId } from "../../store/thunks/invoiceThunk";
import { clearCurrentInvoice } from "../../store/slices/invoiceSlice";
import { fetchAppointmentById } from "../../store/thunks/appointmentsThunk";
import { clearClickedItem } from "../../store/slices/appStateSlice";
import { fetchPaymentsByInvoice, fetchAllPayments, fetchDeposits, fetchPaymentsByClientId } from "../../store/thunks/paymentsThunk";
import ViewInvoicePDFModal from "../../modals/ViewInvoicePDFModal";
import DepositDetailsModal from "../../modals/DepositDetailsModal";

const ClientInvoices = ({ shouldOpenModal = false, selectedInvoice = null, onModalOpened = () => {} }) => {
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [payments, setPayments] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const paymentsLoadedRef = useRef(false);
  const dispatch = useDispatch();
  const currentInvoice = useSelector((state) => state.invoices.currentInvoice);
  const client = useSelector((state) => state.clients.selectedClient);
  const clientInvoices = useSelector((state) => state.invoices.clientInvoices);
  const invoicesStatus = useSelector((state) => state.invoices.status);

  // Prepare data for memoization (with fallbacks to prevent hook issues)
  const invoices = clientInvoices || [];

  // Memoize expensive calculations (must be before early returns)
  const { totalInvoicesAmount, totalPaymentsAmount, totalDepositsAmount, netBalance } = useMemo(() => {
    // Debug: Log all invoice statuses to understand what we're working with
    console.log(`[Client Invoices] Processing ${invoices.length} invoices for outstanding balance calculation`);
    invoices.forEach(inv => {
      console.log(`[Client Invoices] Invoice ${inv.invoiceId}: status="${inv.status}", amount=${inv.amount}`);
    });
    
    // Calculate active invoice total - refunded and voided invoices don't contribute to amount owed
    const activeInvoiceTotal = invoices.reduce((sum, inv) => {
      const status = (inv.status || '').toLowerCase();
      const isExcluded = status === 'refunded' || status === 'void';
      console.log(`[Client Invoices] Invoice ${inv.invoiceId}: status="${inv.status}" (normalized: "${status}"), excluded=${isExcluded}, amount=${inv.amount}`);
      
      if (isExcluded) {
        return sum; // Refunded and voided invoices are no longer owed
      }
      return sum + (parseFloat(inv.amount) || 0);
    }, 0);
    
    console.log(`[Client Invoices] Active invoice total (excluding refunded/voided): ${activeInvoiceTotal}`);
    
    // Calculate total invoice amount for display purposes using same logic as transaction display
    const invoiceTotal = invoices.reduce((sum, inv) => {
      let effectiveAmount = parseFloat(inv.amount) || 0;
      
      if ((inv.status || '').toLowerCase() === 'refunded') {
        // Find all payments for this invoice
        const invoicePayments = payments.filter(pay => 
          !pay.deposit && (
            pay.invoiceId?.invoiceId === inv.invoiceId ||
            (Array.isArray(pay.invoiceId) && pay.invoiceId.some(invRef => invRef.invoiceId === inv.invoiceId))
          )
        );
        
        // Check if all non-deposit payments for this invoice are refunded
        const allPaymentsRefunded = invoicePayments.length > 0 && 
          invoicePayments.every(pay => pay.isRefund || (parseFloat(pay.amount) < 0));
        
        if (allPaymentsRefunded) {
          // If invoice is refunded AND all payments are refunded, invoice contributes $0 to total
          effectiveAmount = 0;
        }
        // If invoice is refunded but payments are NOT refunded, keep full amount for display
      } else if ((inv.status || '').toLowerCase() === 'void') {
        // Voided invoices contribute $0 to display total
        effectiveAmount = 0;
      }
      
      return sum + effectiveAmount;
    }, 0);
    // Calculate net payment effect (refunded payments have net zero effect)
    const nonDepositPayments = payments.filter(pay => !pay.deposit);
    console.log(`[Client Invoices] Filtering deposits from payments - Total payments: ${payments.length}, Non-deposit payments: ${nonDepositPayments.length}`);
    const paymentsTotal = nonDepositPayments.reduce((sum, pay) => {
      const amount = parseFloat(pay.amount) || 0;
      const isRefund = pay.isRefund || (amount < 0);
      if (isRefund) {
        // Refunded payments have net zero effect (payment was made then refunded)
        return sum + 0;
      } else {
        // Normal payments reduce the amount owed
        return sum + Math.abs(amount);
      }
    }, 0);
    // Calculate net deposits total (deposits minus refunds)
    const depositsTotal = deposits.reduce((sum, dep) => {
      const depositAmount = parseFloat(dep.amount) || 0;
      const refundAmount = dep.refund ? (parseFloat(dep.refund.refundAmount) || 0) : 0;
      return sum + depositAmount - refundAmount;
    }, 0);
    const balance = activeInvoiceTotal - paymentsTotal - depositsTotal;
    
    console.log(`[Client Invoices] Final balance calculation: activeInvoiceTotal=${activeInvoiceTotal}, paymentsTotal=${paymentsTotal}, depositsTotal=${depositsTotal}, netBalance=${balance}`);
    
    return {
      totalInvoicesAmount: invoiceTotal,
      totalPaymentsAmount: paymentsTotal,
      totalDepositsAmount: depositsTotal,
      netBalance: balance
    };
  }, [invoices, payments, deposits]);

  // Memoize transaction processing
  const sortedTransactions = useMemo(() => {
    
    // Combine all transactions for journal view
    const invoiceTransactions = invoices.map(inv => {
      // For refunded invoices, check if all associated payments are also refunded
      let effectiveAmount = parseFloat(inv.amount) || 0;
      
      if ((inv.status || '').toLowerCase() === 'refunded') {
        // Find all payments for this invoice
        const invoicePayments = payments.filter(pay => 
          !pay.deposit && (
            pay.invoiceId?.invoiceId === inv.invoiceId ||
            (Array.isArray(pay.invoiceId) && pay.invoiceId.some(invRef => invRef.invoiceId === inv.invoiceId))
          )
        );
        
        // Check if all non-deposit payments for this invoice are refunded
        const allPaymentsRefunded = invoicePayments.length > 0 && 
          invoicePayments.every(pay => pay.isRefund || (parseFloat(pay.amount) < 0));
        
        if (allPaymentsRefunded) {
          // If invoice is refunded AND all payments are refunded, invoice contributes $0 to balance
          effectiveAmount = 0;
        }
        // If invoice is refunded but payments are NOT refunded, keep full amount (creates credit)
      } else if ((inv.status || '').toLowerCase() === 'void') {
        // Voided invoices contribute $0 to balance
        effectiveAmount = 0;
      }
      
      return {
        ...inv,
        _id: `invoice_${inv._id}`, // Ensure unique ID for invoice transactions
        type: 'invoice',
        date: inv.createdAt,
        amount: effectiveAmount,
        description: `Invoice ${inv.invoiceId} - ${inv.services?.[0]?.description || 'Service'}${(inv.status || '').toLowerCase() === 'refunded' ? ' (REFUNDED)' : (inv.status || '').toLowerCase() === 'void' ? ' (VOIDED)' : ''}`,
        runningBalance: 0,
        reference: inv.invoiceId,
        method: null,
        originalInvoiceId: inv._id // Keep reference to original invoice
      };
    });
    
    // Include all payments that are NOT deposits (including refunded payments)
    const paymentTransactions = payments.filter(pay => !pay.deposit).flatMap(pay => {
      const rawAmount = parseFloat(pay.amount) || 0;
      // Detect refunded payments: either has isRefund flag OR has negative amount (which indicates refund)
      const isRefund = pay.isRefund || (rawAmount < 0);
      
      console.log(`[Client Invoices] Processing payment ${pay._id}: isRefund=${isRefund}, amount=${rawAmount}, hasRefundFlag=${pay.isRefund}`);
      
      if (isRefund) {
        // For refunded payments, create TWO transactions: original payment + refund
        return [
          // Original Payment Transaction
          {
            ...pay,
            _id: `payment_original_${pay._id}`, // Unique ID for original payment
            type: 'payment',
            date: pay.paymentDate || pay.createdAt, // Original payment date
            amount: -Math.abs(rawAmount), // Original payment reduces balance
            description: `Payment - ${pay.paymentMethod?.charAt(0).toUpperCase() + pay.paymentMethod?.slice(1) || 'Unknown Method'}`,
            runningBalance: 0,
            reference: pay.invoiceId?.invoiceId || pay.invoice?.invoiceId || 'N/A',
            method: pay.paymentMethod,
            status: 'completed',
            originalPaymentId: pay._id
          },
          // Refund Transaction  
          {
            ...pay,
            _id: `payment_refund_${pay._id}`, // Unique ID for refund
            type: 'refund',
            date: pay.refundedAt || pay.paymentDate || pay.createdAt, // Refund date
            amount: Math.abs(rawAmount), // Refund increases balance back
            description: `Refund - ${pay.paymentMethod?.charAt(0).toUpperCase() + pay.paymentMethod?.slice(1) || 'Unknown Method'}${pay.refundReason ? ` (${pay.refundReason})` : ''}`,
            runningBalance: 0,
            reference: pay.invoiceId?.invoiceId || pay.invoice?.invoiceId || 'N/A',
            method: pay.paymentMethod,
            status: 'completed',
            originalPaymentId: pay._id
          }
        ];
      } else {
        // For normal payments, create single transaction
        return [{
          ...pay,
          _id: `payment_${pay._id}`, // Ensure unique ID for payment transactions
          type: 'payment',
          date: pay.paymentDate || pay.createdAt,
          amount: -Math.abs(rawAmount), // Normal payments reduce balance
          description: `Payment - ${pay.paymentMethod?.charAt(0).toUpperCase() + pay.paymentMethod?.slice(1) || 'Unknown Method'}`,
          runningBalance: 0,
          reference: pay.invoiceId?.invoiceId || pay.invoice?.invoiceId || 'N/A',
          method: pay.paymentMethod,
          status: 'completed',
          originalPaymentId: pay._id
        }];
      }
    });

    const depositTransactions = deposits.flatMap(dep => {
      const transactions = [];
      
      // Add the original deposit transaction
      transactions.push({
        ...dep,
        _id: `deposit_${dep._id}`, // Ensure unique ID for deposit transactions
        type: 'deposit',
        date: dep.paymentDate || dep.createdAt,
        amount: -(parseFloat(dep.amount) || 0),
        description: `Deposit - ${dep.paymentMethod?.charAt(0).toUpperCase() + dep.paymentMethod?.slice(1) || 'Unknown Method'}`,
        runningBalance: 0,
        reference: 'Deposit',
        method: dep.paymentMethod,
        status: 'completed',
        originalDepositId: dep._id // Keep reference to original deposit for modal
      });
      
      // If there's a refund, add the refund transaction
      if (dep.refund) {
        transactions.push({
          ...dep,
          _id: `deposit_refund_${dep._id}`, // Unique ID for refund transaction
          type: 'deposit_refund',
          date: dep.refund.refundDate || dep.paymentDate || dep.createdAt,
          amount: parseFloat(dep.refund.refundAmount) || 0, // Positive amount (increases balance back)
          description: `Deposit Refund - ${dep.refund.refundMethod?.charAt(0).toUpperCase() + dep.refund.refundMethod?.slice(1) || 'Unknown Method'}${dep.refund.refundReason ? ` (${dep.refund.refundReason})` : ''}`,
          runningBalance: 0,
          reference: 'Deposit Refund',
          method: dep.refund.refundMethod,
          status: 'completed',
          originalDepositId: dep._id // Keep reference to original deposit
        });
      }
      
      return transactions;
    });

    const depositUsageTransactions = deposits.flatMap(dep => 
      (dep.depositUsage || []).map((usage, index) => ({
        _id: `deposit_usage_${dep._id}_${index}`, // Create unique composite key
        type: 'deposit_usage',
        date: usage.dateUsed || dep.paymentDate || dep.createdAt,
        amount: -(parseFloat(usage.amountUsed) || 0),
        description: `Deposit Applied - From ${dep.paymentMethod?.charAt(0).toUpperCase() + dep.paymentMethod?.slice(1) || 'Deposit'}`,
        runningBalance: 0,
        reference: usage.invoice?.invoiceId || 'N/A',
        method: dep.paymentMethod,
        status: 'completed',
        originalDepositId: dep._id, // Keep reference to original deposit
        usageData: usage // Keep the original usage data
      }))
    );

    const allTransactions = [
      ...invoiceTransactions,
      ...paymentTransactions,
      ...depositTransactions,
      ...depositUsageTransactions
    ];
    
    // Sort by date for initial display (newest first, with timestamp sub-sorting)
    const sorted = allTransactions.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      // If dates are different, sort by date (newest first)
      if (dateA.getTime() !== dateB.getTime()) {
        return dateB - dateA;
      }
      
      // For same date, use timestamp (newest first)
      const timestampA = new Date(a.createdAt || a.date);
      const timestampB = new Date(b.createdAt || b.date);
      
      return timestampB - timestampA;
    }).map(transaction => ({
      ...transaction,
      runningBalance: 0 // Will be calculated in final sort based on actual display order
    }));
    
    return sorted;
  }, [invoices, payments, deposits]);

  // Memoize final filtering, sorting and recalculate running balance based on final order
  const sortedJournalEntries = useMemo(() => {
    // First apply transaction type filter
    let filteredTransactions;
    if (transactionFilter === 'all') {
      filteredTransactions = [...sortedTransactions];
    } else if (transactionFilter === 'invoices') {
      filteredTransactions = sortedTransactions.filter(t => t.type === 'invoice');
    } else if (transactionFilter === 'payments') {
      filteredTransactions = sortedTransactions.filter(t => t.type === 'payment' || t.type === 'refund');
    } else if (transactionFilter === 'deposits') {
      filteredTransactions = sortedTransactions.filter(t => t.type === 'deposit' || t.type === 'deposit_refund' || t.type === 'deposit_usage');
    } else {
      filteredTransactions = [...sortedTransactions];
    }
    
    // Then apply sorting
    let finalSortedTransactions;
    
    if (!sortConfig.key) {
      finalSortedTransactions = [...filteredTransactions];
    } else {
      finalSortedTransactions = [...filteredTransactions].sort((a, b) => {
        const valueA = sortConfig.key === "amount" || sortConfig.key === "runningBalance"
          ? parseFloat(a[sortConfig.key])
          : a[sortConfig.key];
        const valueB = sortConfig.key === "amount" || sortConfig.key === "runningBalance"
          ? parseFloat(b[sortConfig.key])
          : b[sortConfig.key];

        if (sortConfig.key === "date") {
          const dateA = new Date(valueA);
          const dateB = new Date(valueB);
          
          if (sortConfig.direction === "ascending") {
            // If dates are the same, use timestamp for sub-sorting
            if (dateA.getTime() === dateB.getTime()) {
              const timestampA = new Date(a.createdAt || a.date);
              const timestampB = new Date(b.createdAt || b.date);
              return timestampA - timestampB;
            }
            return dateA - dateB;
          } else {
            // If dates are the same, use timestamp for sub-sorting (newest first)
            if (dateA.getTime() === dateB.getTime()) {
              const timestampA = new Date(a.createdAt || a.date);
              const timestampB = new Date(b.createdAt || b.date);
              return timestampB - timestampA;
            }
            return dateB - dateA;
          }
        }

        if (typeof valueA === "string" && typeof valueB === "string") {
          return sortConfig.direction === "ascending"
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        }

        return sortConfig.direction === "ascending"
          ? valueA - valueB
          : valueB - valueA;
      });
    }
    
    // Recalculate running balance based on the final display order
    // We need to calculate from oldest to newest, so reverse if displaying newest first
    const shouldReverse = !sortConfig.key || (sortConfig.key === "date" && sortConfig.direction !== "ascending");
    const orderedForBalance = shouldReverse ? [...finalSortedTransactions].reverse() : finalSortedTransactions;
    
    let runningBalance = 0;
    const balanceMap = new Map();
    
    orderedForBalance.forEach(transaction => {
      // Only include transactions that affect the actual balance
      // Deposit usage transactions don't affect running balance (money was already counted in original deposit)
      if (transaction.type !== 'deposit_usage') {
        runningBalance += transaction.amount;
      }
      balanceMap.set(transaction._id, runningBalance);
    });
    
    // Apply the calculated balances back to the display-ordered transactions
    return finalSortedTransactions.map(transaction => ({
      ...transaction,
      runningBalance: balanceMap.get(transaction._id)
    }));
  }, [sortedTransactions, sortConfig, transactionFilter]);

  const totalTransactions = sortedTransactions.length;

  const getSplitInvoiceBubble = (transaction) => {
    if (transaction.type !== 'invoice' || !transaction.splitBilling?.isSplit) {
      return null;
    }
    
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200 ml-2">
        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
        Split
      </span>
    );
  };

  // Memoize table columns based on filter state
  const tableColumns = useMemo(() => {
    const baseColumns = [
      {
        header: "Date",
        accessor: (row) => {
          const date = new Date(row.date);
          // For UTC dates (like paymentDate), use UTC methods to avoid timezone conversion
          return date.toLocaleDateString("en-US", {
            weekday: "short",
            year: "numeric",
            month: "short",
            day: "numeric",
            timeZone: "UTC"
          });
        },
        sortable: true,
      },
      {
        header: "Type",
        accessor: (row) => (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.type === 'invoice' ? 'bg-red-100 text-red-800' :
            row.type === 'payment' ? 'bg-green-100 text-green-800' :
            row.type === 'refund' ? 'bg-orange-100 text-orange-800' :
            row.type === 'deposit' ? 'bg-blue-100 text-blue-800' :
            row.type === 'deposit_refund' ? 'bg-red-100 text-red-800' :
            row.type === 'deposit_usage' ? 'bg-purple-100 text-purple-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {row.type === 'deposit_usage' ? 'Deposit Applied' : 
             row.type === 'deposit_refund' ? 'Deposit Refund' :
             row.type.charAt(0).toUpperCase() + row.type.slice(1)}
          </span>
        ),
        sortable: true,
      },
      {
        header: "Reference",
        accessor: (row) => (
          <div className="flex items-center">
            <span className="text-sm text-gray-600">
              {row.reference}
            </span>
            {getSplitInvoiceBubble(row)}
          </div>
        ),
        sortable: true,
      },
      {
        header: "Description",
        accessor: (row) => (
          <div className="flex items-center gap-2">
            <span>{row.description}</span>
            {getStatusBubble(row)}
          </div>
        ),
        sortable: true,
      },
      {
        header: "Amount",
        accessor: (row) => (
          <span className={getTransactionTypeColor(row.type)}>
            {formatCurrency(row.amount)}
          </span>
        ),
        sortable: true,
      },
    ];

    // Only add running balance column when showing all transactions
    if (transactionFilter === 'all') {
      baseColumns.push({
        header: "Running Balance",
        accessor: (row) => (
          <span className={`font-medium ${
            row.runningBalance >= 0 ? 'text-orange-600' : 'text-blue-600'
          }`}>
            {formatCurrency(row.runningBalance)}
          </span>
        ),
        sortable: true,
      });
    }

    return baseColumns;
  }, [transactionFilter]);

  useEffect(() => {
    if (client?._id) {
      paymentsLoadedRef.current = false; // Reset when client changes
      dispatch(fetchInvoicesByClientId(client._id));
    }
  }, [dispatch, client?._id]);

  // Handle automatic modal opening from search navigation
  useEffect(() => {
    if (shouldOpenModal && selectedInvoice && selectedInvoice._id) {
      logInfo("Opening invoice modal from search", { invoiceId: selectedInvoice._id });
      
      // Load the invoice and open the modal
      const loadAndShowInvoice = async () => {
        try {
          setIsLoadingInvoice(true);
          await dispatch(fetchInvoiceById(selectedInvoice._id));
          setShowInvoiceGenerator(true);
          onModalOpened(); // Notify parent that modal was opened
          
          // Clear the clickedItem from Redux to prevent the modal from reopening
          console.log("[Client Invoices] Clearing clickedItem to prevent modal re-opening");
          dispatch(clearClickedItem());
        } catch (error) {
          console.error("Error loading selected invoice:", error);
        } finally {
          setIsLoadingInvoice(false);
        }
      };
      
      loadAndShowInvoice();
    }
  }, [shouldOpenModal, selectedInvoice, dispatch, onModalOpened]);

  // Separate useEffect for payments and deposits that depends on invoices being loaded initially
  useEffect(() => {
    if (client?._id && clientInvoices && clientInvoices.length > 0 && !paymentsLoadedRef.current) {
      paymentsLoadedRef.current = true;
      loadPaymentsAndDeposits();
    }
  }, [dispatch, client?._id, clientInvoices]);

  const loadPaymentsAndDeposits = async () => {
    if (!client?._id) return;
    
    try {
      
      // First try the new client-specific payments route
      try {
        const clientPaymentsResponse = await dispatch(fetchPaymentsByClientId(client._id)).unwrap();
        setPayments(clientPaymentsResponse.payments || []);
      } catch (paymentsError) {
        
        // Fallback 1: Fetch all payments and filter by client
        try {
          const allPaymentsResponse = await dispatch(fetchAllPayments({ limit: 1000, dateFilter: 'all' })).unwrap();
          console.log('All payments response:', allPaymentsResponse);
          
          const clientPayments = allPaymentsResponse.payments?.filter(payment => {
            const paymentClientId = payment.invoiceId?.client?._id || payment.invoice?.client?._id;
            console.log('Checking payment:', payment, 'Payment Client ID:', paymentClientId, 'Target Client ID:', client._id);
            return paymentClientId === client._id;
          }) || [];
          
          console.log('Filtered client payments:', clientPayments);
          setPayments(clientPayments);
        } catch (allPaymentsError) {
          console.log('No payments found via general fetch:', allPaymentsError);
          
          // Fallback 2: Try to get payments for each invoice 
          if (clientInvoices && clientInvoices.length > 0) {
            console.log('Trying invoice-specific fetch...');
            const invoicePayments = [];
            for (const invoice of clientInvoices) {
              try {
                const invoicePaymentsResponse = await dispatch(fetchPaymentsByInvoice(invoice._id)).unwrap();
                console.log(`Payments for invoice ${invoice.invoiceId}:`, invoicePaymentsResponse);
                if (invoicePaymentsResponse.payments) {
                  invoicePayments.push(...invoicePaymentsResponse.payments);
                }
              } catch (err) {
                console.log(`No payments found for invoice ${invoice.invoiceId}:`, err.message);
              }
            }
            console.log('Invoice-specific payments:', invoicePayments);
            setPayments(invoicePayments);
          } else {
            setPayments([]);
          }
        }
      }
      
      // Fetch deposits for this client
      try {
        const depositsResponse = await dispatch(fetchDeposits(client._id)).unwrap();
        console.log('Deposits response:', depositsResponse);
        setDeposits(depositsResponse.deposits || []);
      } catch (depositsError) {
        console.log('No deposits found for client:', depositsError);
        // This is ok - just means no deposits exist
        setDeposits([]);
      }
      
    } catch (error) {
      console.error('Error loading payments and deposits:', error);
    }
  };

  if (!client) {
    return (
      <div className="p-4 text-gray-600">Loading client information...</div>
    );
  }

  if (invoicesStatus === "loading") {
    return (
      <div className="p-4 text-gray-600">Loading invoices...</div>
    );
  }


  const handleSortChange = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    } else if (
      sortConfig.key === key &&
      sortConfig.direction === "descending"
    ) {
      direction = null;
      key = null;
    }
    setSortConfig({ key, direction });
  };


  const handleInvoiceGeneratorClose = () => {
    console.log("[Client Invoices] Closing invoice modal and clearing clickedItem");
    setShowInvoiceGenerator(false);
    dispatch(clearCurrentInvoice());
    dispatch(clearClickedItem()); // Clear to prevent reopening on tab changes
  };

  const handleDepositModalClose = () => {
    setShowDepositModal(false);
    setSelectedDeposit(null);
  };

  const handleInvoiceClickFromDeposit = async (invoice) => {
    if (!invoice || !invoice._id) {
      console.error("Invalid invoice data for click");
      return;
    }
    
    // Close deposit modal first
    handleDepositModalClose();
    
    // Load and show invoice details
    setIsLoadingInvoice(true);
    try {
      const invoiceData = await dispatch(fetchInvoiceById(invoice._id)).unwrap();
      if (invoiceData.appointmentId) {
        await dispatch(fetchAppointmentById(invoiceData.appointmentId));
      }
      await dispatch(fetchPaymentsByInvoice(invoice._id));
      setShowInvoiceGenerator(true);
    } catch (error) {
      console.error("Error loading invoice from deposit:", error);
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  const handleRowClick = async (clickedItemId) => {
    
    console.log("=== ROW CLICK DEBUG ===");
    console.log("Row clicked with ID:", clickedItemId);
    
    // Prevent multiple clicks while loading
    if (isLoadingInvoice) {
      console.log("Already loading invoice, ignoring click");
      return;
    }
    
    // Find the full transaction object from our data
    const clickedTransaction = sortedJournalEntries.find(transaction => transaction._id === clickedItemId);
    console.log("Found transaction:", clickedTransaction);
    console.log("Transaction type:", clickedTransaction?.type);
    
    if (!clickedTransaction) {
      console.error("Transaction not found for ID:", clickedItemId);
      return;
    }
    
    if (clickedTransaction.type === 'invoice') {
      console.log("Processing invoice click for invoice:", clickedTransaction);
      setIsLoadingInvoice(true);
      
      try {
        // Use the original invoice ID for fetching
        const originalInvoiceId = clickedTransaction.originalInvoiceId;
        console.log("Fetching invoice with ID:", originalInvoiceId);
        const invoice = await dispatch(fetchInvoiceById(originalInvoiceId)).unwrap();
        console.log("Fetched invoice data:", invoice);

        if (invoice.appointmentId) {
          console.log("Fetching appointment with ID:", invoice.appointmentId);
          await dispatch(fetchAppointmentById(invoice.appointmentId));
        }
        
        console.log("Fetching payments for invoice ID:", invoice._id);
        await dispatch(fetchPaymentsByInvoice(invoice._id));
        
        console.log("Opening invoice generator modal from table click");
        setShowInvoiceGenerator(true);
        
        // Clear clickedItem to prevent conflicts with search-triggered modals
        dispatch(clearClickedItem());
      } catch (error) {
        console.error("Error loading invoice details:", error);
      } finally {
        setIsLoadingInvoice(false);
      }
    } else if (clickedTransaction.type === 'deposit') {
      console.log("Processing deposit click for deposit:", clickedTransaction);
      // Find the original deposit from our deposits array using the originalDepositId
      const originalDepositId = clickedTransaction.originalDepositId;
      const originalDeposit = deposits.find(dep => dep._id === originalDepositId);
      if (originalDeposit) {
        setSelectedDeposit(originalDeposit);
        setShowDepositModal(true);
      } else {
        console.error("Could not find original deposit data for ID:", originalDepositId);
      }
    } else {
      console.log("Non-clickable transaction type:", clickedTransaction.type);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'invoice': return 'text-red-600';
      case 'payment': return 'text-green-600';
      case 'refund': return 'text-orange-600';
      case 'deposit': return 'text-blue-600';
      case 'deposit_refund': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBubble = (transaction) => {
    let status, bubbleClass, statusText, dotColor;
    
    if (transaction.type === 'invoice') {
      status = transaction.status?.toLowerCase() || 'pending';
      
      switch (status) {
        case 'paid':
        case 'completed':
          bubbleClass = 'bg-green-100 text-green-800 border-green-200';
          statusText = 'Paid';
          dotColor = 'bg-green-400';
          break;
        case 'partial':
        case 'partially paid':
          bubbleClass = 'bg-yellow-100 text-yellow-800 border-yellow-200';
          statusText = 'Partial';
          dotColor = 'bg-yellow-400';
          break;
        case 'pending':
        case 'outstanding':
        case 'unpaid':
          bubbleClass = 'bg-orange-100 text-orange-800 border-orange-200';
          statusText = 'Pending';
          dotColor = 'bg-orange-400';
          break;
        case 'void':
          bubbleClass = 'bg-gray-100 text-gray-800 border-gray-200';
          statusText = 'Void';
          dotColor = 'bg-gray-400';
          break;
        case 'cancelled':
        case 'canceled':
          bubbleClass = 'bg-red-100 text-red-800 border-red-200';
          statusText = 'Cancelled';
          dotColor = 'bg-red-400';
          break;
        case 'overdue':
          bubbleClass = 'bg-red-100 text-red-800 border-red-200';
          statusText = 'Overdue';
          dotColor = 'bg-red-400';
          break;
        case 'refunded':
          bubbleClass = 'bg-purple-100 text-purple-800 border-purple-200';
          statusText = 'Refunded';
          dotColor = 'bg-purple-400';
          break;
        default:
          bubbleClass = 'bg-gray-100 text-gray-800 border-gray-200';
          statusText = status.charAt(0).toUpperCase() + status.slice(1);
          dotColor = 'bg-gray-400';
      }
    } else if (transaction.type === 'payment') {
      bubbleClass = 'bg-green-100 text-green-800 border-green-200';
      statusText = 'Completed';
      dotColor = 'bg-green-400';
    } else if (transaction.type === 'refund') {
      bubbleClass = 'bg-orange-100 text-orange-800 border-orange-200';
      statusText = 'Refunded';
      dotColor = 'bg-orange-400';
    } else if (transaction.type === 'deposit') {
      bubbleClass = 'bg-blue-100 text-blue-800 border-blue-200';
      statusText = 'Received';
      dotColor = 'bg-blue-400';
    } else if (transaction.type === 'deposit_refund') {
      bubbleClass = 'bg-red-100 text-red-800 border-red-200';
      statusText = 'Refunded';
      dotColor = 'bg-red-400';
    } else if (transaction.type === 'deposit_usage') {
      bubbleClass = 'bg-purple-100 text-purple-800 border-purple-200';
      statusText = 'Applied';
      dotColor = 'bg-purple-400';
    } else {
      return null;
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${bubbleClass}`}>
        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${dotColor}`}></span>
        {statusText}
      </span>
    );
  };

  return (
    <div data-component="ClientInvoices" className="space-y-6">
      {/* Summary Boxes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-600">Total Invoices</p>
              <p className="text-3xl font-bold text-red-900">{formatCurrency(totalInvoicesAmount)}</p>
              <p className="text-sm text-red-600 mt-1">{invoices.length} invoices</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Total Payments & Deposits</p>
              <p className="text-3xl font-bold text-green-900">{formatCurrency(totalPaymentsAmount + totalDepositsAmount)}</p>
              <p className="text-sm text-green-600 mt-1">{payments.length + deposits.length} transactions</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className={`${netBalance >= 0 ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'} border rounded-lg p-6`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${netBalance >= 0 ? 'text-orange-600' : 'text-blue-600'}`}>
                Net {netBalance >= 0 ? 'Outstanding' : 'Credit'}
              </p>
              <p className={`text-3xl font-bold ${netBalance >= 0 ? 'text-orange-900' : 'text-blue-900'}`}>
                {formatCurrency(netBalance)}
              </p>
              <p className={`text-sm mt-1 ${netBalance >= 0 ? 'text-orange-600' : 'text-blue-600'}`}>
                {netBalance >= 0 ? 'Amount owed' : 'Credit balance'}
              </p>
            </div>
            <div className={`p-3 rounded-full ${netBalance >= 0 ? 'bg-orange-100' : 'bg-blue-100'}`}>
              <svg className={`w-6 h-6 ${netBalance >= 0 ? 'text-orange-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Journal Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Financial Journal</h3>
              <p className="text-sm text-gray-600 mt-1">
                Complete financial history for {client.firstName} {client.lastName} ({totalTransactions} total transactions)
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label htmlFor="transaction-filter" className="text-sm font-medium text-gray-700">
                  Filter:
                </label>
                <select
                  id="transaction-filter"
                  value={transactionFilter}
                  onChange={(e) => setTransactionFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-40"
                >
                  <option value="all">All Transactions</option>
                  <option value="invoices">Invoices Only</option>
                  <option value="payments">Payments Only</option>
                  <option value="deposits">Deposits Only</option>
                </select>
              </div>
              {isLoadingInvoice && (
                <div className="flex items-center text-sm text-blue-600">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading invoice...
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="overflow-auto max-h-96">
          <Table
            columns={tableColumns}
            data={sortedJournalEntries}
            onRowClick={(id) => handleRowClick(id)}
            showPagination={false}
            onSortChange={handleSortChange}
            sortConfig={sortConfig}
          />
        </div>
      </div>
      {showInvoiceGenerator && currentInvoice && (
        <ViewInvoicePDFModal
          onClose={handleInvoiceGeneratorClose}
        />
      )}
      {showDepositModal && selectedDeposit && (
        <DepositDetailsModal
          deposit={selectedDeposit}
          onClose={handleDepositModalClose}
          onInvoiceClick={handleInvoiceClickFromDeposit}
        />
      )}
    </div>
  );
};

export default ClientInvoices;
